import { useMemo, useState } from 'react'
import { Pause, Play, RefreshCw, Archive, MoreHorizontal } from 'lucide-react'
import { Button } from '../ui/Button'
import { AskEyebrow } from '../today/AskEyebrow'
import { ORI_KIND_LABEL } from '../../utils/oriAsks'

const KEEP_PAUSED_PRESETS: Array<{ id: string; label: string; days: number }> = [
  { id: 'one_more_week', label: 'Another week', days: 7 },
  { id: 'two_more_weeks', label: '2 more weeks', days: 14 },
  { id: 'one_more_month', label: 'One more month', days: 30 },
]

function buildPauseUntilIso(daysFromNow: number): string {
  const d = new Date()
  d.setDate(d.getDate() + daysFromNow)
  return d.toISOString()
}

export function HabitPauseRevisitCard({
  item,
  onRestart,
  onAdjust,
  onKeepPaused,
  onArchive,
}: {
  item: { habit_def_id: number; habit_title: string; message?: string }
  onRestart: (habitDefId: number) => void | Promise<unknown>
  onAdjust?: (habitDefId: number) => void | Promise<unknown>
  onKeepPaused?: (
    habitDefId: number,
    payload: { paused_until: string; source: string }
  ) => void | Promise<unknown>
  onArchive?: (
    habitDefId: number,
    payload: { source: string; reason?: string }
  ) => void | Promise<unknown>
}) {
  const [working, setWorking] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const [keepDays, setKeepDays] = useState<number>(7)

  const keepLabel = useMemo(() => {
    const preset = KEEP_PAUSED_PRESETS.find(p => p.days === keepDays)
    return preset ? preset.label.toLowerCase() : `${keepDays} more days`
  }, [keepDays])

  const guarded = (fn: () => Promise<unknown> | void) => async () => {
    if (working) return
    setWorking(true)
    try {
      await Promise.resolve(fn())
    } finally {
      setWorking(false)
    }
  }

  const handleKeep = guarded(() => {
    if (!onKeepPaused) return
    return onKeepPaused(item.habit_def_id, {
      paused_until: buildPauseUntilIso(keepDays),
      source: 'auto_mom_revisit_card',
    })
  })

  const handleArchive = guarded(() => {
    if (!onArchive) return
    return onArchive(item.habit_def_id, {
      source: 'auto_mom_revisit_card',
    })
  })

  return (
    <div className="p-3 rounded-xl border border-soft bg-surface">
      <AskEyebrow icon={<Pause />} label={ORI_KIND_LABEL.decide} typeWord="Habit" className="mb-1" />
      <p className="text-sm font-semibold text-primary">{item.habit_title}</p>
      <p className="mt-1 text-xs text-secondary">{item.message}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={working}
          onClick={guarded(() => onRestart(item.habit_def_id))}
          className="bg-brand hover:bg-brand-hover text-on-brand"
        >
          <Play className="w-3.5 h-3.5 mr-1" /> Restart
        </Button>
        {onAdjust && (
          <Button
            size="sm"
            variant="outline"
            disabled={working}
            onClick={guarded(() => onAdjust(item.habit_def_id))}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Adjust
          </Button>
        )}
        {(onKeepPaused || onArchive) && (
          <Button
            size="sm"
            variant="ghost"
            disabled={working}
            onClick={() => setShowMore(prev => !prev)}
          >
            <MoreHorizontal className="w-3.5 h-3.5 mr-1" /> {showMore ? 'Less' : 'More'}
          </Button>
        )}
      </div>

      {showMore && (
        <div className="mt-3 space-y-3 border-t border-soft pt-3">
          {onKeepPaused && (
            <div>
              <p className="text-xs font-medium text-primary">Keep paused for…</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {KEEP_PAUSED_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    type="button"
                    className={`rounded-full border px-2 py-1 text-xs ${
                      keepDays === preset.days
                        ? 'border-brand bg-brand-subtle text-brand'
                        : 'border-soft text-secondary'
                    }`}
                    onClick={() => setKeepDays(preset.days)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={working}
                onClick={handleKeep}
                className="mt-2"
              >
                Keep paused {keepLabel}
              </Button>
            </div>
          )}
          {onArchive && (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-secondary">
                Done with {item.habit_title}? Archive it. Your history is preserved.
              </p>
              <Button size="sm" variant="ghost" disabled={working} onClick={handleArchive}>
                <Archive className="w-3.5 h-3.5 mr-1" /> Archive
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
